/**
 * Channels Page
 * 
 * Manage Twitch channels connected to PratBot.
 * Add, remove, start/stop bot in channels.
 */

import { useCallback, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api, { ApiError, apiErrorMessage } from '@/api/client'
import { useToast } from '@/components/Toast'
import {
  Plus,
  Play,
  Square,
  Trash2,
  Radio,
  AlertCircle,
  Loader2,
  ChevronRight,
} from 'lucide-react'
import clsx from 'clsx'

/**
 * Channel data from the API.
 */
interface Channel {
  id: number
  owner_id: number
  twitch_channel_id: string
  twitch_channel_name: string
  command_prefix: string
  bot_enabled: boolean
  bot_connected: boolean
  settings: Record<string, unknown>
  enabled_modules: string[]
}

/**
 * Channel card component.
 */
function ChannelCard({
  channel,
  onStart,
  onStop,
  onDelete,
  onClick,
  isLoading,
}: {
  channel: Channel
  onStart: (e: React.MouseEvent) => void
  onStop: (e: React.MouseEvent) => void
  onDelete: (e: React.MouseEvent) => void
  onClick: () => void
  isLoading: boolean
}) {
  // Derive status from bot_connected and bot_enabled
  const getStatus = () => {
    if (!channel.bot_enabled) return 'disabled'
    if (channel.bot_connected) return 'connected'
    return 'disconnected'
  }
  
  const status = getStatus()
  
  const statusColors = {
    connected: 'text-synth-mint',
    disconnected: 'text-state-warning',
    disabled: 'text-text-muted',
  }
  
  const statusBg = {
    connected: 'bg-synth-mint',
    disconnected: 'bg-state-warning',
    disabled: 'bg-neutral-600',
  }
  
  const statusLabels = {
    connected: 'Connected',
    disconnected: 'Disconnected',
    disabled: 'Disabled',
  }
  
  return (
    <div
      className="card cursor-pointer hover:bg-surface-overlay hover:border-border-strong transition-colors group"
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-synth-magenta/20 flex items-center justify-center">
            <Radio className="text-synth-magenta" size={24} />
          </div>
          <div>
            <h3 className="font-semibold text-text-primary flex items-center gap-2">
              #{channel.twitch_channel_name}
              <ChevronRight 
                size={16} 
                className="text-text-muted group-hover:text-text-secondary transition-colors" 
              />
            </h3>
            <div className="flex items-center gap-2 text-sm">
              <div
                className={clsx(
                  'w-2 h-2 rounded-full',
                  statusBg[status]
                )}
              />
              <span className={statusColors[status]}>
                {statusLabels[status]}
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {isLoading ? (
            <div className="p-2">
              <Loader2 className="animate-spin text-text-secondary" size={18} />
            </div>
          ) : channel.bot_connected ? (
            <button
              onClick={onStop}
              className="p-2 text-text-secondary hover:text-state-danger hover:bg-surface-overlay rounded-lg transition-colors"
              title="Stop bot"
            >
              <Square size={18} />
            </button>
          ) : (
            <button
              onClick={onStart}
              className="p-2 text-text-secondary hover:text-synth-mint hover:bg-surface-overlay rounded-lg transition-colors"
              title="Start bot"
            >
              <Play size={18} />
            </button>
          )}
          <button
            onClick={onDelete}
            className="p-2 text-text-secondary hover:text-state-danger hover:bg-surface-overlay rounded-lg transition-colors"
            title="Remove channel"
            disabled={isLoading}
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
      
      <div className="flex items-center justify-between text-sm">
        <div className="text-text-secondary">
          <span>Prefix: </span>
          <code className="bg-surface-overlay px-2 py-0.5 rounded text-text-primary">
            {channel.command_prefix}
          </code>
        </div>
        {channel.enabled_modules.length > 0 && (
          <div className="text-text-muted">
            {channel.enabled_modules.length} module{channel.enabled_modules.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Add channel modal component.
 */
function AddChannelModal({
  isOpen,
  onClose,
  onAdd,
  isAdding,
  error,
}: {
  isOpen: boolean
  onClose: () => void
  onAdd: (channelName: string, prefix: string) => void
  isAdding: boolean
  error: string | null
}) {
  const [channelName, setChannelName] = useState('')
  const [prefix, setPrefix] = useState('!')
  
  if (!isOpen) return null
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (channelName.trim() && !isAdding) {
      onAdd(channelName.trim(), prefix)
    }
  }
  
  const handleClose = () => {
    if (!isAdding) {
      setChannelName('')
      setPrefix('!')
      onClose()
    }
  }
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative bg-surface-overlay rounded-xl border border-border p-6 w-full max-w-md">
        <h2 className="text-xl font-semibold text-text-primary mb-4">Add Channel</h2>
        
        {error && (
          <div className="mb-4 p-3 bg-state-danger-bg border border-state-danger rounded-lg flex items-center gap-2 text-state-danger text-sm">
            <AlertCircle size={16} />
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm text-text-secondary mb-2">
              Twitch Channel Name
            </label>
            <input
              type="text"
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              placeholder="e.g., techrium"
              className="input"
              autoFocus
              disabled={isAdding}
            />
            <p className="text-xs text-text-muted mt-2">
              Enter the Twitch username of the channel to add.
            </p>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm text-text-secondary mb-2">
              Command Prefix
            </label>
            <input
              type="text"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value.slice(0, 5))}
              placeholder="!"
              className="input w-20"
              maxLength={5}
              disabled={isAdding}
            />
            <p className="text-xs text-text-muted mt-2">
              Commands will start with this prefix (default: !)
            </p>
          </div>
          
          <div className="flex gap-3">
            <button 
              type="button" 
              onClick={handleClose} 
              className="btn-secondary flex-1"
              disabled={isAdding}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              disabled={isAdding || !channelName.trim()}
            >
              {isAdding ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Adding...
                </>
              ) : (
                'Add Channel'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/**
 * Channels page component.
 */
export default function Channels() {
  const navigate = useNavigate()
  const [channels, setChannels] = useState<Channel[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)
  const [loadingChannels, setLoadingChannels] = useState<Set<number>>(new Set())

  const { success, error } = useToast()

  /**
   * Every per-row channel write goes through here: busy set, then toast.
   *
   * `call` returns the server's own message where it sends one (start/stop name
   * the channel), and nothing where the outcome is fully known here.
   */
  const mutate = useCallback(
    async (
      channelId: number,
      call: () => Promise<string | void>,
      successMessage: string,
      failureMessage: string,
    ) => {
      setLoadingChannels((prev) => new Set(prev).add(channelId))
      try {
        const detail = await call()
        success(typeof detail === 'string' && detail ? detail : successMessage)
      } catch (err) {
        error(apiErrorMessage(err, failureMessage))
      } finally {
        setLoadingChannels((prev) => {
          const next = new Set(prev)
          next.delete(channelId)
          return next
        })
      }
    },
    [success, error],
  )
  
  // Fetch channels
  useEffect(() => {
    async function fetchChannels() {
      try {
        const data = await api.get<Channel[]>('/api/channels')
        setChannels(data)
        setLoadError(null)
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.status === 501) {
            // Not implemented yet - show placeholder
            setChannels([])
          } else {
            // Fatal load failure stays INLINE, not a toast.
            setLoadError(apiErrorMessage(err, 'Failed to load channels'))
          }
        } else {
          setLoadError('Failed to load channels')
        }
      } finally {
        setIsLoading(false)
      }
    }
    
    fetchChannels()
  }, [])
  
  // Add channel
  const handleAddChannel = async (channelName: string, prefix: string) => {
    setIsAdding(true)
    setAddError(null)
    
    try {
      const newChannel = await api.post<Channel>('/api/channels', {
        twitch_channel_name: channelName,
        command_prefix: prefix,
      })
      setChannels((prev) => [...prev, newChannel])
      setShowAddModal(false)
      success(`Added #${newChannel.twitch_channel_name}`)
    } catch (err) {
      // Stays INLINE in the modal, not a toast: the form is still open and the
      // user is about to correct the name a self-dismissing message would hide.
      setAddError(apiErrorMessage(err, 'Failed to add channel'))
    } finally {
      setIsAdding(false)
    }
  }
  
  // Start bot in channel
  const handleStart = async (e: React.MouseEvent, channelId: number) => {
    e.stopPropagation()  // Prevent card click
    await mutate(
      channelId,
      async () => {
        const result = await api.post<{ status: string; channel: string; message: string }>(
          `/api/channels/${channelId}/start`
        )
        setChannels((prev) =>
          prev.map((ch) =>
            ch.id === channelId ? { ...ch, bot_enabled: true, bot_connected: true } : ch
          )
        )
        return result.message
      },
      'Bot started',
      'Failed to start bot',
    )
  }
  
  // Stop bot in channel
  const handleStop = async (e: React.MouseEvent, channelId: number) => {
    e.stopPropagation()  // Prevent card click
    await mutate(
      channelId,
      async () => {
        const result = await api.post<{ status: string; channel: string; message: string }>(
          `/api/channels/${channelId}/stop`
        )
        setChannels((prev) =>
          prev.map((ch) =>
            ch.id === channelId ? { ...ch, bot_enabled: false, bot_connected: false } : ch
          )
        )
        return result.message
      },
      'Bot stopped',
      'Failed to stop bot',
    )
  }
  
  // Delete channel
  const handleDelete = async (e: React.MouseEvent, channelId: number) => {
    e.stopPropagation()  // Prevent card click
    const channel = channels.find((ch) => ch.id === channelId)
    if (!channel) return
    
    if (!confirm(`Are you sure you want to remove #${channel.twitch_channel_name}?`)) {
      return
    }
    
    await mutate(
      channelId,
      async () => {
        await api.delete(`/api/channels/${channelId}`)
        setChannels((prev) => prev.filter((ch) => ch.id !== channelId))
      },
      `Removed #${channel.twitch_channel_name}`,
      'Failed to remove channel',
    )
  }
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-synth-magenta"></div>
      </div>
    )
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Channels</h2>
          <p className="text-text-secondary">
            Manage the Twitch channels where PratBot operates.
          </p>
        </div>
        <button
          onClick={() => {
            setAddError(null)
            setShowAddModal(true)
          }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={18} />
          Add Channel
        </button>
      </div>
      
      {/* Fatal load failure — INLINE by design, not a toast */}
      {loadError && (
        <div className="bg-state-danger-bg border border-state-danger rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="text-state-danger" size={20} />
          <span className="text-state-danger">{loadError}</span>
        </div>
      )}
      
      {/* Channels grid */}
      {channels.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {channels.map((channel) => (
            <ChannelCard
              key={channel.id}
              channel={channel}
              onStart={(e) => handleStart(e, channel.id)}
              onStop={(e) => handleStop(e, channel.id)}
              onDelete={(e) => handleDelete(e, channel.id)}
              onClick={() => navigate(`/channels/${channel.id}`)}
              isLoading={loadingChannels.has(channel.id)}
            />
          ))}
        </div>
      ) : (
        <div className="card text-center py-12">
          <Radio className="mx-auto text-text-muted mb-4" size={48} />
          <h3 className="text-lg font-medium text-text-primary mb-2">No channels yet</h3>
          <p className="text-text-secondary mb-6">
            Add your first channel to get started.
          </p>
          <button
            onClick={() => {
              setAddError(null)
              setShowAddModal(true)
            }}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus size={18} />
            Add Channel
          </button>
        </div>
      )}
      
      {/* Add channel modal */}
      <AddChannelModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddChannel}
        isAdding={isAdding}
        error={addError}
      />
    </div>
  )
}
